<script setup>

    import moment from 'moment'

    import Dropdown from '@/Components/App/Dropdown.vue'
    import Loader from '@/Components/App/Loader.vue'
    import StaffDropdown from '@/Components/Forms/StaffDropdown.vue'
    import { useAuthStore, useClientSingleStore, useConfigStore, useStaffStore, useNotificationStore } from '@/Stores'
    import { storeToRefs } from 'pinia'
    import { computed, ref, watch, onMounted } from 'vue'
    import { util, CARE_RECIPIENT_CONTACT_TYPE, getModifiedProperties, deepCopy, getOptionName, handleBlur } from '@/Helpers'
    import { useStorage } from '@vueuse/core'
    import { useRouter } from 'vue-router'

    import { EnvelopeIcon, PhoneArrowUpRightIcon, UserIcon, UserPlusIcon, ClipboardDocumentListIcon, MapIcon, CakeIcon, MagnifyingGlassIcon } from '@heroicons/vue/24/solid'
    import { ExclamationTriangleIcon, CheckBadgeIcon, TrophyIcon } from '@heroicons/vue/16/solid'
    import LwxCoffee from '@/Components/LwxCoffee.vue'
    import LwxSelect from '@/Components/Forms/LwxSelect.vue'
    import Address from '@/Components/Forms/Address.vue'
    import NameInput from '@/Components/Forms/NameInput.vue'
    import InputLabel from '@/Components/Forms/InputLabel.vue'
    import TextInput from '@/Components/Forms/TextInput.vue'
    import ClientDisposition from '@/Components/Forms/ClientDisposition.vue'
    import ClientDataCompletionStatus from '@/Components/Client/ClientDataCompletionStatus.vue'
    import LatteRatingBar from '@/Components/Feedback/LatteRatingBar.vue'
    import Modal from '@/Components/App/Modal.vue'
    import PrimaryButton from '@/Components/Forms/PrimaryButton.vue'
    import SecondaryButton from '@/Components/Forms/SecondaryButton.vue'

    import DatePicker from '@/Components/Forms/DatePicker.vue'
    import { IconHeartPulse24Filled } from '@iconify-prerendered/vue-fluent'

    const clientCoffeeBaseUrl = import.meta.env.VITE_COFFEE_BASEURL + '/staff/sales-manager/information/pid/'
    const router = useRouter()
    const authStore = useAuthStore()
    const clientSingleStore = useClientSingleStore()
    const { dispositionsRequiringReasons } = storeToRefs(clientSingleStore)
    const configStore = useConfigStore()
    const { config } = storeToRefs(configStore)
    const { user } = storeToRefs(authStore)
    const staffStore = useStaffStore()
    const notificationStore = useNotificationStore()

    const props = defineProps({
        enable: {
            type: Boolean,
            default: true
        },
        isEdit: {
            type: Boolean,
            default: false
        }
    })

    const { client } = storeToRefs(clientSingleStore)

    const emit = defineEmits(['client:store', 'client:update', 'client:cancel-update', 'contact:selected', 'contact:addNewPrimary'])

    const localClient = ref(clientSingleStore.defaultClient())

    const clientPropsChange = ref(false)
    const loading = ref(true)

    const clientStatusData = computed({
        get: () => ({
            clientType: localClient.value.type,
            disposition: localClient.value.disposition
        }),
        set: (value) => {
            localClient.value.type = value.clientType
            localClient.value.disposition = value.disposition
        }
    })

    const clientStatusReasonsOptions = computed(() => {
        return clientSingleStore.getDispositionReasonOptionsList(localClient.value.disposition);
    })

    const types = ref([
        {
            id: null,
            name: ""
        }
    ])

    watch(config, (value) => {
        types.value = value.contact.types.map(item => ({
            id: item,
            name: item
        }))
        .filter(item => item.id !== CARE_RECIPIENT_CONTACT_TYPE);
    }, { deep: true, immediate : true })

    const hasClientData = computed(() => {
        const hasData = client.value && client.value.id
        return hasData;
    })

    const clientName = computed(() => {
        if (client.value.first_name && client.value.last_name) {
            return client.value.first_name + ' ' + client.value.last_name;
        }

        return client.value.first_name || client.value.last_name || 'Unnamed Client';
    })

    const clientAddress = computed(() => {
        const address = client.value.address?.address ?? '';
        const address_2 = client.value.address?.address_2 ?? '';
        const city = client.value.address?.city ?? '';
        const state = client.value.address?.state ?? '';
        const zip = client.value.address?.zip ?? '';

        const addressParts = [address, address_2, city, state].filter(part => part !== '');
        const addressComplete = `${addressParts.join(', ')} ${zip}`.trim();
        return addressComplete.length > 1 ? addressComplete : "(no address)";
    })

    const clientBirthday = computed(() => {
        if (localClient.value.birthday !== null && localClient.value.birthday !== '') {
            const birthday = moment(localClient.value.birthday, 'YYYY-MM-DD').isValid()
            if (birthday) {
                return localClient.value.birthday;
            }
        }
        return false;
    })

    const clientAge = computed(() => {
        return clientBirthday.value ? moment().diff(moment(clientBirthday.value, 'YYYY-MM-DD'), 'years') : null;
    })

    const genderText = computed(() => {
        if (localClient.value.gender && localClient.value.gender !== 'other') {
            return util.titleCase(localClient.value.gender);
        }
        return 'Missing Gender, ';
    })

    const phoneLinks = computed(() => {
        const links = []
        client.value.phone_numbers?.forEach(phone => {
            if (!phone.number) { return; }
            links.push({
                label: 'Call +' + phone.country_code + '-' + util.addDashesToPhone(phone.number) + ' (' + phone.type + ')',
                href: 'tel:+' + phone.country_code + phone.number
            })
        })
        return links;
    })

    const emailLinks = computed(() => {
        const links = []
        client.value.emails?.forEach(email => {
            if (!email || email == '') { return; }
            links.push({
                label: 'Email ' + email,
                href: 'mailto:' + email
            })
        })
        return links;
    })

    const basicClientInfo = computed(() => {
        let info =  'Client: ' + clientName.value + ' Address: ' + clientAddress.value + ' Phone: '
        const cell = client.value.phone_numbers?.find(phone => phone.type === 'cell')
        if (cell?.number) {
            info += util.addDashesToPhone(cell.number)
        } else {
            const home = client.value.phone_numbers?.find(phone => phone.type === 'home')
            if (home?.number) {
                info += util.addDashesToPhone(home.number)
            }
        }
        return info;
    })

    const primaryContactIndexes = computed(() => {
        const contactIndex = []

        localClient.value.contacts.forEach((contact, index) => {
            if (contact.primary_contact) {
                contactIndex.push(index)
            }
        })
        return contactIndex;
    })

    watch(() => client.value, (value) => {
        if (hasClientData) {
            clientPropsChange.value = true
            const oldContacts = deepCopy(localClient.value.contacts)
            localClient.value = deepCopy(value)
            localClient.value.contacts = combineContacts(oldContacts, value.contacts)
        }
    }, {immediate: true, deep: true})

    function combineContacts(oldContacts, newContacts) {
        const result = []
        const changedContacts = deepCopy(newContacts)
        for (const i in oldContacts) {
            if (!oldContacts[i].id) {
                if (util.isEmpty(oldContacts[i].first_name) && util.isEmpty(oldContacts[i].last_name) && util.isEmpty(oldContacts[i].type)) {
                    result.push(deepCopy(oldContacts[i]))
                } else {
                    const index = changedContacts.findIndex(item => item.uuid == oldContacts[i].uuid)
                    if (index != -1) {
                        result.push(transformContactSelects(deepCopy(changedContacts[index])))
                    }
                }
            } else {
                const index = changedContacts.findIndex(item => item.uuid == oldContacts[i].uuid)
                if (index != -1) {
                    result.push(transformContactSelects(deepCopy(changedContacts[index])))
                    changedContacts.splice(index, 1)
                }
            }
        }

        for (const i in changedContacts) {
            const index = result.findIndex(item => item.uuid == changedContacts[i].uuid)
            if (index == -1) {
                result.push(deepCopy(changedContacts[i]))
            }
        }
        return result;
    }

    const handleStoreChange = (data) => {
        if (Object.keys(data).length > 0) {
            emit('client:store', data, 'profile_client_intro');
        }
    }

    const handleUpdateChange = (data) => {
        if (Object.keys(data).length > 0) {
            emit('client:update', data, 'profile_client_intro');
        } else {
            emit('client:cancel-update', client.value.id);
        }
    }

    // watch(() => localClient.value.type, (newValue, oldValue) => {
    //     if (newValue !== oldValue) {
    //         console.log('Clearing disposition reasons due to type change')
    //         localClient.value.disposition_reasons = []
    //     }
    // }, { immediate: true, deep: true })

    // watch(() => localClient.value.disposition, (newValue, oldValue) => {
    //     if (newValue !== oldValue) {
    //         console.log('Clearing disposition reasons due to disposition change')
    //         localClient.value.disposition_reasons = []
    //     }
    // }, { immediate: true, deep: true })

    watch(localClient, (newValue, oldValue) => {
        if (oldValue === undefined) {
            clientPropsChange.value = false;
            return;
        }
        if (newValue.id && oldValue.id === newValue.id) {

            if(clientPropsChange.value == false) {
                const diff = getModifiedProperties(
                    JSON.stringify(clientSingleStore.extractClient(newValue)),
                    JSON.stringify(client.value),
                    [
                        'first_name', 'last_name',
                        'type', 'disposition',
                        'quick_recall', 'disposition_reasons',
                        'service_level', 'number_of_adults',
                        'estimated_start_date', 'estimated_end_date', 'follow_up_date',
                        {
                            key: 'address',
                            diff: (newObj, oldObj) => {
                                return (newObj.address != oldObj.address ||
                                    newObj.address_2 != oldObj.address_2 ||
                                    newObj.city != oldObj.city ||
                                    newObj.state != oldObj.state ||
                                    newObj.zip != oldObj.zip)
                                ? newObj : null;
                            }
                        },
                        {
                            key: 'assigned_sales_manager',
                            diff: (newObj, oldObj) => {
                                return newObj.id != oldObj.id ? newObj : null;
                            }
                        },
                        {
                            key: 'contacts',
                            diff: (newArr, oldArr) => {
                                const oldArrDict = {}
                                const diff = []
                                oldArr.forEach(contact => oldArrDict[contact.id] = contact)

                                for (const i in newArr) {
                                    if (!newArr[i].id) {
                                        if (!util.isEmpty(newArr[i].first_name) || !util.isEmpty(newArr[i].last_name || !util.isEmpty(newArr[i].type))) {
                                            diff.push(newArr[i])
                                        }
                                    } else {
                                        if (newArr[i].first_name != oldArrDict[newArr[i].id].first_name ||
                                            newArr[i].last_name != oldArrDict[newArr[i].id].last_name ||
                                            newArr[i].type != oldArrDict[newArr[i].id].type) {
                                            diff.push(newArr[i])
                                        }
                                    }
                                }
                                return diff.length ? diff : null;
                            }
                        }
                    ]
                )
                if (diff.disposition && !diff.type) {
                    diff.type = localClient.value.type
                }
                if (diff.disposition || diff.type) {
                    diff['assigned_sales_manager'] = localClient.value.assigned_sales_manager
                }
                handleUpdateChange(diff)
                return;
            }
        } else if (clientPropsChange.value == false) {
            const diff = getModifiedProperties(
                JSON.stringify(clientSingleStore.extractClient(newValue)),
                JSON.stringify(client.value),
                [
                    'first_name', 'last_name',
                    'type', 'disposition',
                    'quick_recall', 'estimated_start_date', 'follow_up_date',
                    {
                        key: 'address',
                        diff: (newObj, oldObj) => {
                            return (newObj.address != oldObj?.address ||
                                newObj.address_2 != oldObj?.address_2 ||
                                newObj.city != oldObj?.city ||
                                newObj.state != oldObj?.state ||
                                newObj.zip != oldObj?.zip)
                            ? newObj : null;
                        }
                    },
                    {
                        key: 'contacts',
                        diff: (newArr, oldArr) => {
                            if (newArr.length == 0) {
                                return null;
                            }

                            const diff = []
                            for (const i in newArr) {
                                if (!util.isEmpty(newArr[i].first_name) || !util.isEmpty(newArr[i].last_name || !util.isEmpty(newArr[i].type))) {
                                    diff.push(newArr[i])
                                }
                            }

                            return diff.length ? diff : null;
                        }
                    }
                ]
            )
            if (Object.keys(diff).length > 0) {
                diff['assigned_sales_manager'] = localClient.value.assigned_sales_manager
            }
            handleStoreChange(diff)
        }
        clientPropsChange.value = false
    }, { immediate: true, deep: true })

    function handleAssignedStaffChange(staff) {
        localClient.value.assigned_sales_manager = staff
    }

    function handleAddressUpdate(newAddress) {
        localClient.value.address = newAddress;
    }

    function selectPrimaryContact(contact) {
        emit('contact:selected', contact.id);
        util.scrollToElement('contact')
    }

    const contactsCollapsed = useStorage('contact-collapsed', false)

    function selectAddNewContact() {
        emit('contact:addNewPrimary');
        contactsCollapsed.value = false
        util.scrollToElement('contact')
    }

    function pushNewPrimaryContact() {
        localClient.value.contacts.push({
            first_name: null,
            last_name: null,
            primary_contact: true,
            type: null,
            uuid: crypto.randomUUID(),
        })
    }

    onMounted(async () => {
        staffStore.getAllStaffs()
            .then(response => {
                    rehydrateStaffs(response.data)
                })
    })

    async function hydrateStaffs(data) {
        await staffStore.hydrateAllStaffs(data)
        loading.value = false
    }

    function rehydrateStaffs(data) {
        loading.value = true
        hydrateStaffs(data)
    }

    async function saveToClipboard(text) {
        if (document.hasFocus()) {
            await navigator.clipboard.writeText(text)
        }
    }

    const maxSelectedLabelsForReasons = computed(() => {
        return localClient.value.disposition_reasons?.includes('insurance_medicaid') ? 1 : 2;
    })

    function transformContactSelects(proxyContact) {
        return {
            ...proxyContact,
            type: getOptionName(proxyContact.type, types.value) ? {id: proxyContact.type, name: getOptionName(proxyContact.type, types.value)} : proxyContact.type,
        };
    }



    const overallProgress = computed(() => {
        return clientSingleStore.getProfileDataCompletionStatus(client.value?.data_completion_status);
    })

    const progressIcon = computed(() => {
        switch(overallProgress.value) {
            case 'great': return TrophyIcon;
            case 'good': return CheckBadgeIcon;
            case 'poor': return ExclamationTriangleIcon;
            default: return ExclamationTriangleIcon;
        }
    })

    const progressColor = computed(() => {
        switch(overallProgress.value) {
            case 'great': return 'text-lifeworx-green-600';
            case 'good': return 'text-lifeworx-green-600';
            case 'poor': return 'text-lifeworx-orange-600';
            default: return 'text-gray-400';
        }
    })

    const progressText = computed(() => {
        switch(overallProgress.value) {
            case 'great': return 'Great';
            case 'good': return 'Good';
            case 'poor': return 'Poor';
            default: return 'None';
        }
    })

    const showSearchConfirmation = ref(false)

    function navigateToSearch() {
        showSearchConfirmation.value = false
        router.push({ name: 'search.caregivers.client', params: { client_id: client.value.id } })
    }

    function findMatchingCaregivers() {
        const isStale = !client.value.aggregate_updated_at || moment().diff(moment(client.value.aggregate_updated_at), 'months') > 3
        if (overallProgress.value === 'poor' || isStale) {
            showSearchConfirmation.value = true
        } else {
            navigateToSearch()
        }
    }

</script>

<template>

    <div
        v-if="!props.enable"
        class="sm:col-span-1 pt-4 pb-8 md:py-0 md:col-span-7 md:pr-6 md:border-r md:border-r-stone-500 md:border-opacity-20" >
        <Loader />
    </div>

    <template v-else>

        <div class="sm:col-span-1 pt-4 pb-8 md:py-0 md:col-span-7 md:pr-6 md:border-r md:border-r-stone-500 md:border-opacity-20">

            <template
                v-if="(hasClientData || props.isEdit) && !loading"
            >

                <div class="mt-2 grid grid-cols-8 gap-8">

                    <div class="col-span-5">
                        <ClientDisposition
                            v-model="clientStatusData"
                            :title="client?.disposition_updated_at ? `Updated ${moment(client?.disposition_updated_at).fromNow()}` : ''"
                        />
                    </div>

                    <div class="col-span-3">

                        <div v-if="client?.data_completion_status" class="mb-2.5">
                            <div class="flex items-center mb-2">
                                <h5 class="text-stone-500 font-semibold text-xs tracking-wide mr-4">
                                    Data Completion
                                </h5>
                                <div class="flex items-center gap-0.5 opacity-75">
                                    <component :is="progressIcon" :class="['size-4', progressColor]" />
                                    <span :class="['text-sm font-semibold', progressColor]">{{ progressText }}</span>
                                </div>
                            </div>
                            <ClientDataCompletionStatus
                                :subject="client"
                                subjectType="client"
                                palette="alabaster"
                            />
                        </div>

                    </div>

                </div>



                <div v-if="!props.isEdit">

                    <h1
                        @click="clientName === 'Unnamed Client' ? util.scrollToElement('contact') : null"
                        :class="[
                            'font-serif text-4xl font-medium py-2',
                            {
                                'text-lifeworx-blue-800 cursor-default': clientName !== 'Unnamed Client',
                                'text-stone-400 italic cursor-pointer': clientName === 'Unnamed Client'
                            }
                        ]"
                    >
                        {{ clientName }}
                    </h1>

                    <div
                        class="font-bold flex items-center"
                    >

                        <CakeIcon
                            class="pr-2 text-right h-8 w-8 select-none"
                            :class="localClient.birthday ? 'text-lifeworx-blue-600' : 'text-lifeworx-red-500'"
                        />

                        <VTooltip
                            v-if="localClient.birthday"
                            :triggers="['hover']"
                            :autoHide="true"
                            @apply-hide="handleBlur"
                        >
                            <a
                                @click="util.scrollToElement('contact')"
                                class="text-lifeworx-blue-800 hover:text-lifeworx-blue-600 cursor-pointer"
                            >{{ genderText }}{{ clientAge !== null ? ', ' + clientAge + ' years old' : '' }}</a>

                            <template #popper>
                                <span class="text-sm">
                                    Born {{ clientBirthday }}
                                </span>
                            </template>

                        </VTooltip>

                        <a
                            v-else
                            class="text-lifeworx-red-500 cursor-pointer hover:text-lifeworx-red-600"
                            @click="util.scrollToElement('contact')"
                        >{{ genderText }} Missing Date of Birth</a>
                    </div>

                    <div
                        v-if="primaryContactIndexes.length"
                        class="pt-1 pb-1"
                    >
                        <UserIcon
                            class="inline-flex h-5 w-5 mr-2 text-lifeworx-blue-800 hover:text-lifeworx-blue-700 cursor-pointer"
                        />
                        <template
                            v-for="(index, i) in primaryContactIndexes" :key="index"
                        >
                            <span
                                class="cursor-pointer font-bold text-sm text-lifeworx-blue-800"
                                @click="selectPrimaryContact(localClient.contacts[index])"
                            >
                                <template v-if="localClient.contacts[index].first_name">{{ localClient.contacts[index].first_name }}</template>
                                <template v-if="localClient.contacts[index].first_name && localClient.contacts[index].last_name">&nbsp;</template>
                                <template v-if="localClient.contacts[index].last_name">{{ localClient.contacts[index].last_name }}</template>
                                <template v-if="typeof localClient.contacts[index].type == 'string'">&nbsp;&lpar;{{ localClient.contacts[index].type }}&rpar;</template>
                                <template v-else-if="localClient.contacts[index].type">&nbsp;&lpar;{{ getOptionName(localClient.contacts[index].type.id, types) }}&rpar;</template>
                            </span>
                            <template v-if="i < primaryContactIndexes.length - 1">, </template>
                        </template>
                    </div>

                    <div
                        class="pt-1 pb-5"
                    >
                        <span
                            class="cursor-pointer group font-bold underline text-sm text-lifeworx-blue-600 hover:text-lifeworx-blue-700"
                            @click="selectAddNewContact"
                        >
                            <UserPlusIcon
                                class="inline-flex h-5 w-5 mr-2 text-lifeworx-blue-600 cursor-pointer group-hover:text-lifeworx-blue-700"
                            />{{ 'Add a Primary Contact' }}
                        </span>
                    </div>

                    <div
                        v-if="client.address"
                        class="font-bold text-sm text-lifeworx-blue-800 flex items-center"
                    >



                        <MapIcon
                            class="pr-2 text-right h-6 w-6 text-xs select-none"
                            :class="clientAddress !== '(no address)' ? 'text-lifeworx-blue-600' : 'text-lifeworx-red-500'"
                        />

                        <VTooltip
                            v-if="clientAddress !== '(no address)'"
                            :triggers="['hover']"
                            :autoHide="true"
                            @apply-hide="handleBlur"
                        >
                            <a
                                :href="'https://maps.google.com/?q=' + encodeURIComponent(clientAddress)"
                                target="_blank"
                                class="text-lifeworx-blue-800 hover:text-lifeworx-blue-600"
                            >{{ clientAddress }}</a>

                            <template #popper>
                                <span class="text-sm">
                                    Open Google Maps
                                </span>
                            </template>

                        </VTooltip>

                        <a
                            v-else
                            class="text-lifeworx-red-500 cursor-pointer hover:text-lifeworx-red-600"
                            @click="util.scrollToElement('contact')"
                        >Missing Address</a>
                    </div>
                </div>

                <div v-else>

                    <div class="flex flex-wrap -mx-3 mb-3">

                        <div class="md:w-1/2 pl-3 pr-1 mb-4 md:mb-0">
                            <NameInput
                                type="text"
                                v-model="localClient.first_name"
                                label="First Name"
                            >
                            </NameInput>
                        </div>

                        <div class="md:w-1/2 pl-1 pr-3 mb-4 md:mb-0">
                            <NameInput
                                type="text"
                                v-model="localClient.last_name"
                                label="Last Name"
                            >
                            </NameInput>
                        </div>

                    </div>

                    <div
                        v-if="primaryContactIndexes.length"
                        class="pt-1 pb-1"
                    >
                        <template
                            v-for="(index, i) in primaryContactIndexes" :key="index"
                        >
                            <div class="flex flex-wrap items-end pl-3 -mx-3 mb-3">

                                <UserPlusIcon
                                    class="flex-none inline-flex h-5 mb-2 w-5 text-lifeworx-blue-800"
                                />

                                <div class="md:w-44 pl-3 pr-1 mb-4 md:mb-0">
                                    <InputLabel
                                        :for="`primary-contact-first-name-${index}`"
                                        value="First Name"
                                        class="text-xs text-stone-400"
                                    >
                                        <TextInput
                                            class="w-full px-2.5 py-1.5 text-sm font-semibold bg-lifeworx-alabaster-200 text-lifeworx-blue-800"
                                            type="text"
                                            :id="`primary-contact-first-name-${index}`"
                                            v-model="localClient.contacts[index]['first_name']"
                                        />
                                    </InputLabel>
                                </div>
                                <div class="md:w-44 pl-2 pr-1 mb-4 md:mb-0">
                                    <InputLabel
                                        :for="`primary-contact-last-name-${index}`"
                                        value="Last Name"
                                        class="text-xs text-stone-400"
                                    >
                                        <TextInput
                                            class="w-full px-2.5 py-1.5 text-sm font-semibold bg-lifeworx-alabaster-200 text-lifeworx-blue-800"
                                            type="text"
                                            :id="`primary-contact-first-name-${index}`"
                                            v-model="localClient.contacts[index]['last_name']"
                                        />
                                    </InputLabel>
                                </div>
                                <div class="md:w-44 pl-2 pr-1 mb-4 md:mb-0">
                                    <InputLabel
                                        :for="`primary-contact-type-${index}`"
                                        value="Type"
                                        class="text-xs text-stone-400"
                                    >
                                        <LwxSelect
                                            input_class="w-full px-2.5 py-1.5 text-sm font-semibold bg-lifeworx-alabaster-200 text-lifeworx-blue-800"
                                            :id="`primary-contact-type-${index}`"
                                            v-model="localClient.contacts[index]['type']"
                                            :options="types"
                                        />
                                    </InputLabel>
                                </div>
                            </div>
                        </template>
                    </div>

                    <div
                        class="pt-1 pb-5"
                    >
                        <span
                            class="cursor-pointer font-bold underline text-sm text-lifeworx-blue-600 group hover:text-lifeworx-blue-700"
                            @click="pushNewPrimaryContact"
                        >
                            <UserPlusIcon
                                class="inline-flex h-5 w-5 mr-2 text-lifeworx-blue-600 cursor-pointer group-hover:text-lifeworx-blue-700"
                            />{{ 'Add a Primary Contact' }}
                        </span>
                    </div>

                    <div>
                        <Address
                            :address="localClient.address"
                            @update:address="handleAddressUpdate"
                        >
                        </Address>
                    </div>
                </div>

                <div class="flex items-center mb-4 md:mb-0">
                    <div class="grid grid-cols-3">

                        <div class="mt-2">
                            <Dropdown
                                v-if="clientSingleStore.clientHasPhoneNumber(client)"
                                :links="phoneLinks"
                                direction="right"
                            >
                                <template #button>
                                    <VTooltip
                                        :triggers="['hover']"
                                        :autoHide="true"
                                        @apply-hide="handleBlur"
                                    >
                                        <PhoneArrowUpRightIcon
                                            class="inline-flex h-6 w-6 text-lifeworx-blue-600 hover:text-lifeworx-blue-500 cursor-pointer"
                                        />
                                        <template #popper>
                                            <span class="text-sm">
                                                Phone Numbers
                                            </span>
                                        </template>
                                    </VTooltip>
                                </template>
                            </Dropdown>

                            <PhoneArrowUpRightIcon
                                v-else
                                class="inline-flex m-2 h-6 w-6 text-zinc-300"
                            />
                        </div>

                        <div class="mt-2">
                            <Dropdown
                                v-if="client?.emails && client.emails.length > 0"
                                :links="emailLinks"
                                direction="right"
                            >
                                <template #button>
                                    <VTooltip
                                        :triggers="['hover']"
                                        :autoHide="true"
                                        @apply-hide="handleBlur"
                                    >
                                        <EnvelopeIcon
                                            class="inline-flex h-6 w-6 text-lifeworx-blue-600 hover:text-lifeworx-blue-500 cursor-pointer"
                                        />
                                        <template #popper>
                                            <span class="text-sm">
                                                Email Addresses
                                            </span>
                                        </template>
                                    </VTooltip>
                                </template>
                            </Dropdown>

                            <EnvelopeIcon
                                v-else
                                class="inline-flex m-2 h-6 w-6 text-zinc-300"
                            />
                        </div>

                        <div class="mt-2">
                            <VTooltip
                                :triggers="['hover']"
                                :autoHide="true"
                                @apply-hide="handleBlur"
                            >
                                <ClipboardDocumentListIcon
                                    v-if="client.id"
                                    class="inline-flex m-2 h-6 w-6 text-lifeworx-blue-600 hover:text-lifeworx-blue-500 cursor-pointer"
                                    @click="saveToClipboard(basicClientInfo).then(() => { notificationStore.add('Client Info Copied To Clipboard') })"
                                />

                                <ClipboardDocumentListIcon
                                    v-else
                                    class="inline-flex m-2 h-6 w-6 text-zinc-300"
                                />

                                <template #popper>
                                    <span class="text-sm">
                                        Copy Client Info
                                    </span>
                                </template>

                            </VTooltip>
                        </div>

                    </div>

                    <div class="flex items-center mx-4 mt-2">
                        <div class="h-6 w-0.5 bg-stone-300"></div>
                    </div>

                    <div class="flex items-center text-sm font-bold text-lifeworx-blue-600 mt-2">

                        <VTooltip
                            v-if="client.coffee_user_profile_id"
                            :triggers="['hover']"
                            :autoHide="true"
                            @apply-hide="handleBlur"
                        >

                            <a
                                :href="clientCoffeeBaseUrl + client.coffee_user_profile_id"
                                target="_blank"
                                class="hover:text-lifeworx-blue-500 hover:underline group text-center"
                            >
                                <LwxCoffee
                                    class="inline-flex -mt-1 mr-2 h-7 w-7"
                                />
                                <span class="group-hover:underline">Coffee ID {{ client.coffee_user_profile_id }}</span>
                            </a>

                            <template #popper>
                                <span class="text-sm">
                                    Open Coffee Profile
                                </span>
                            </template>

                        </VTooltip>

                        <span
                            v-else
                        >
                            <LwxCoffee
                                class="inline-flex -mt-1 mr-2 h-7 w-7 text-zinc-300"
                            />
                            New Profile
                        </span>
                    </div>

                    <template v-if="client.is_healthcare">
                        <div class="flex items-center justify-center mx-4 mt-2">
                            <div class="h-6 w-0.5 bg-stone-300"></div>
                        </div>

                        <div class="flex items-center text-sm font-bold text-lifeworx-red-600 mt-2 animate-pulse cursor-help select-none">
                            <VTooltip
                                :triggers="['hover']"
                                :autoHide="true"
                                @apply-hide="handleBlur"
                            >
                                <div class="flex items-center">
                                    <IconHeartPulse24Filled
                                        class="mr-2 size-6 text-lifeworx-red-600"
                                    />
                                    Healthcare
                                </div>
                                <template #popper>
                                    <span class="text-sm">
                                        Marked as Healthcare in QuickBooks
                                    </span>
                                </template>
                            </VTooltip>
                        </div>
                    </template>
                </div>

                <div
                    v-if="user.is_administrator"
                    class="mt-4">
                    <div
                        v-if="!((hasClientData || props.isEdit) && !loading)"
                        class="h-10"
                    >
                        <Skeleton
                            height="100%"
                            class="h-10 bg-lifeworx-alabaster-200/75"
                        />
                    </div>
                    <StaffDropdown
                        v-else
                        :staff="client.assigned_sales_manager"
                        @assigned-staff:update="handleAssignedStaffChange"
                    />
                </div>

            </template>

            <Loader v-else />

        </div>

        <div class="sm:col-span-1 pt-4 pb-8 md:py-0 md:col-span-5">

            <div class="grid gap-y-6 mt-3">

                <div v-if="client?.type === 'client'" class="grid grid-cols-3 gap-6">

                    <div class="col-span-2">
                        <h5 class="text-stone-500 font-semibold text-xs tracking-wide mb-2">
                            Client Feedback
                        </h5>
                        <LatteRatingBar
                            :subject="client"
                            :subjectType="client.type"
                        />
                    </div>

                    <div class="col-span-1">
                        <h5 class="text-stone-500 font-semibold text-xs tracking-wide mb-0.5">
                            Total Revenue
                        </h5>
                        <span class="text-xl font-bold text-lifeworx-blue-800">
                            {{ client && client.total_revenue !== null && client.total_revenue !== undefined
                                ? util.currencyWithoutFraction(client.total_revenue)
                                : '—' }}
                        </span>
                    </div>


                </div>

                <div v-if="dispositionsRequiringReasons.includes(localClient.disposition)">
                    <InputLabel
                        for="client-basicinfo-reasons"
                        :value="`Reason for ${util.titleCase(util.underscoreToSpace(localClient.disposition))} Disposition`"
                        class="text-xs font-semibold text-stone-400"
                    >
                        <Skeleton
                            v-if="loading"
                            height="34px"
                            class="border bg-lifeworx-blue-800/10 border-lifeworx-blue-800/20"
                        />
                        <MultiSelect
                            v-else
                            id="client-basicinfo-reasons"
                            v-model="localClient.disposition_reasons"
                            :options="clientStatusReasonsOptions"
                            optionLabel="name"
                            optionValue="id"
                            :maxSelectedLabels="maxSelectedLabelsForReasons"
                            class="w-full"
                            display="chip"
                            :pt="{
                                root: { class: 'text-xs bg-lifeworx-alabaster-200 outline-1 -outline-offset-1 outline-stone-300 hover:outline-stone-400 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm/6' },
                                overlay: { class: 'text-sm' },
                                header: { class: 'hidden'}
                            }"
                        />
                    </InputLabel>
                </div>

                <div
                    v-if="(localClient.type === 'lead' || localClient.type === 'prospect') && ![ 'disqualified', 'no_opportunity', 'terminated' ].includes(localClient.disposition)"
                >
                    <InputLabel
                        for="estimated_start_date"
                        value="Estimated Start Date"
                        class="text-xs font-semibold text-stone-400"
                    >
                        <Skeleton
                            v-if="!((hasClientData || props.isEdit) && !loading)"
                            height="34px"
                            class="border bg-lifeworx-blue-800/10 border-lifeworx-blue-800/20"
                        />
                        <DatePicker
                            v-else
                            class="theme-alabaster"
                            id="estimated_start_date"
                            :min-date="props.start_date"
                            v-model="localClient.estimated_start_date"
                            :teleport="true"
                            :clearable="false"
                        />
                    </InputLabel>
                </div>

                <div
                >
                    <InputLabel
                        for="follow_up_date"
                        value="Follow-Up Date"
                        class="text-xs font-semibold text-stone-400"
                    >
                        <Skeleton
                            v-if="!((hasClientData || props.isEdit) && !loading)"
                            height="34px"
                            class="border bg-lifeworx-blue-800/10 border-lifeworx-blue-800/20"
                        />
                        <DatePicker
                            v-else
                            class="theme-alabaster"
                            id="follow_up_date"
                            v-model="localClient.follow_up_date"
                            :teleport="true"
                            :clearable="true"
                        />
                    </InputLabel>
                </div>

                <div>
                    <InputLabel
                        for="quick-recall-note"
                        value="Quick Recall"
                        class="text-xs text-stone-400"
                    >
                        <textarea
                            v-model="localClient.quick_recall"
                            id="quick-recall-note"
                            class="w-full h-32 text-sm font-semibold bg-lifeworx-alabaster-200 border-stone-400 placeholder:text-stone-400 text-lifeworx-blue-800 focus:border-lifeworx-blue-700 focus:ring-lifeworx-blue-700 focus:ring-1 focus:outline-none rounded-md shadow-sm peer tracking-wide resize-y"
                        />
                    </InputLabel>
                </div>

                <template v-if="configStore.featureEnabled('search-caregivers')">
                    <div>
                        <PrimaryButton
                            v-if="client.id"
                            class="w-full"
                            button_style="text-white bg-lifeworx-green-600 hover:bg-lifeworx-green-500 focus:bg-lifeworx-green-600 active:bg-lifeworx-green-700 focus:ring-lifeworx-green-400"
                            @click="findMatchingCaregivers"
                        >
                            <MagnifyingGlassIcon class="h-5 w-5 mr-2 -ml-1" />
                            Start Caregiver Search
                        </PrimaryButton>
                    </div>
                </template>

            </div>

        </div>

    </template>

    <Modal :show="showSearchConfirmation" max-width="lg" @close="showSearchConfirmation = false">
        <div class="pb-4">
            <div class="sm:flex sm:items-start">
                <div class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-lifeworx-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon
                        class="h-6 w-6 text-lifeworx-orange-600"
                        aria-hidden="true"
                    />
                </div>
                <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <h3 class="text-base font-semibold leading-6 text-lifeworx-blue-800">
                        {{ overallProgress === 'poor' && (!client.aggregate_updated_at || moment().diff(moment(client.aggregate_updated_at), 'months') > 3)
                            ? 'Data Quality & Freshness Warning'
                            : overallProgress === 'poor'
                                ? 'Data Quality Warning'
                                : 'Data Freshness Warning'
                        }}
                    </h3>
                    <div class="mt-2">
                        <p class="text-sm text-stone-500">
                            <template v-if="overallProgress === 'poor' && (!client.aggregate_updated_at || moment().diff(moment(client.aggregate_updated_at), 'months') > 3)">
                                This {{ client.type }} profile is missing key information and has not been updated recently, which may affect search quality. Consider adding more details and updating the profile before searching for caregivers.
                            </template>
                            <template v-else-if="overallProgress === 'poor'">
                                This {{ client.type }} profile is missing key information which may affect search quality. Consider adding more details before searching for caregivers.
                            </template>
                            <template v-else>
                                This {{ client.type }} profile has not been updated recently, which may affect search quality. Consider reviewing and updating the profile before searching for caregivers.
                            </template>
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <div class="sm:flex sm:flex-row-reverse">
            <PrimaryButton
                class="w-full sm:w-auto sm:ml-3"
                @click="navigateToSearch"
            >
                Start Search Anyway
            </PrimaryButton>

            <SecondaryButton
                class="mt-3 w-full sm:mt-0 sm:w-auto"
                @click="showSearchConfirmation = false"
            >
                Go Back
            </SecondaryButton>
        </div>
    </Modal>


</template>
